
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ArrowDown, ArrowUp, Fan, Thermometer, AlertTriangle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

const ChillerPlantOverview = () => {
  // Updated chiller plant data
  const plantData = {
    totalCapacity: 1500,
    currentLoad: 1013,
    loadPercentage: 67,
    inletTemp: 12.4,
    outletTemp: 6.65,
    chillers: [
      { id: 1, name: 'Chiller 1', status: 'online', load: 75 },
      { id: 2, name: 'Chiller 2', status: 'online', load: 65 },
      { id: 3, name: 'Chiller 3', status: 'standby', load: 0, attention: true },
    ],
    totalEfficiency: 85.2,
    totalPower: 421.6, // Sum of all chillers power consumption
    efficiencyChange: 3.1
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Chiller Plant Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Current Load</p>
              <p className="text-2xl font-semibold">{plantData.currentLoad.toLocaleString()} kW</p>
              <p className="text-sm text-muted-foreground">of {plantData.totalCapacity.toLocaleString()} kW</p>
            </div>

            <div className="text-right">
              <p className="text-sm text-muted-foreground">Plant Efficiency</p>
              <p className="text-2xl font-semibold">{plantData.totalEfficiency}%</p>
              <p className="text-sm text-emerald-500">+{plantData.efficiencyChange}% from last month</p>
            </div>
          </div>

          <div className="mt-2">
            <div className="flex justify-between mb-1">
              <span className="text-sm">Load</span>
              <span className="text-sm font-medium">{plantData.loadPercentage}%</span>
            </div>
            <Progress value={plantData.loadPercentage} className="h-2" />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900">
                <ArrowDown className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Inlet Temperature</p>
                <p className="text-xl font-medium">{plantData.inletTemp}°C</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900">
                <ArrowUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outlet Temperature</p>
                <p className="text-xl font-medium">{plantData.outletTemp}°C</p>
              </div>
            </div>
          </div>
          
          <div className="border-t pt-3 mt-3">
            <h4 className="text-sm font-medium mb-2">Chiller Status</h4>
            <div className="grid grid-cols-3 gap-2">
              {plantData.chillers.map((chiller) => (
                <div 
                  key={chiller.id} 
                  className={`border rounded-md p-2 text-center ${chiller.attention ? 'border-amber-500' : ''}`}
                >
                  <p className="font-medium">{chiller.name}</p>
                  <div className={`text-xs inline-block px-2 py-0.5 rounded-full my-1 ${
                    chiller.status === 'online' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 
                    'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
                  }`}>
                    {chiller.status === 'online' ? 'Online' : 'Standby'}
                  </div>
                  {chiller.status === 'online' && (
                    <p className="text-sm">{chiller.load}% load</p>
                  )}
                  {chiller.attention && (
                    <div className="flex items-center justify-center gap-1 mt-1 text-xs text-amber-600">
                      <AlertTriangle className="h-3 w-3" /> Attention
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChillerPlantOverview;
